import { Injectable, inject, signal } from '@angular/core';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import {
  Firestore,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  getAggregateFromServer,
  sum,
  count,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  KpiCard,
  OpsColumn,
} from '../components/admin/admin-dashboard/admin-dashboard.models';

interface AdminOpsMetrics {
  noIbanCount:      number;
  newOwnersCount:   number;
  staleDraftsCount: number;
}

/**
 * Admin KPI service — fetches platform metrics with minimal Firestore reads:
 *
 *   • 1 aggregation query  → commission, GMV, tx count               (costs 1 read)
 *   • platform expense docs → actual Stripe fees paid by TimeFlexRent
 *   • 1 getDocs on assets  → distinct active owner IDs                (costs N reads, N = active spaces)
 *
 * Call loadKpis() once per dashboard visit; no real-time listeners needed here.
 */
@Injectable({ providedIn: 'root' })
export class AdminDashboardService extends FirebaseAwareService {
  private readonly db        = inject(Firestore);
  private readonly functions = inject(Functions);

  readonly loading  = signal(false);
  readonly loadError = signal<string | null>(null);

  // ── KPIs (4 stable entries — ids never change, Angular reuses canvas elements) ──
  readonly kpis = signal<KpiCard[]>([
    {
      id: 'net-commission',
      label: 'Comisión neta (mes)',
      value: '—',
      deltaLabel: 'Cargando…',
      deltaPositive: true,
      sparkline: [0, 0, 0, 0, 0, 0, 0],
    },
    {
      id: 'gmv',
      label: 'GMV mensual',
      value: '—',
      deltaLabel: 'Alquiler total gestionado',
      deltaPositive: true,
      sparkline: [0, 0, 0, 0, 0, 0, 0],
    },
    {
      id: 'avg-ticket',
      label: 'Ticket medio comisión',
      value: '—',
      deltaLabel: 'Eficiencia vs. coste Stripe',
      deltaPositive: true,
      sparkline: [0, 0, 0, 0, 0, 0, 0],
    },
    {
      id: 'active-owners',
      label: 'Propietarios activos',
      value: '—',
      deltaLabel: 'Con ≥1 espacio publicado',
      deltaPositive: true,
      sparkline: [0, 0, 0, 0, 0, 0, 0],
    },
  ]);

  // ── Ops queues — seeded with skeleton; loadOpsColumns() fills real counts ────
  readonly opsColumns = signal<OpsColumn[]>([
    {
      id: 'bookings',
      title: 'Flujo de reservas',
      icon: 'calendar-clock',
      items: [
        {
          id: 'pending-form',
          label: 'Por formalizar (plazo activo)',
          count: 0,
          urgency: 'critical',
          route: '/gestionar-reservas',
          queryParams: { filter: 'pending_formalization' },
        },
        {
          id: 'pending-verif',
          label: 'Por verificar (propietario)',
          count: 0,
          urgency: 'warning',
          route: '/gestionar-reservas',
          queryParams: { filter: 'pending_verification' },
        },
        {
          id: 'expired',
          label: 'Formalizaciones expiradas',
          count: 0,
          urgency: 'normal',
          route: '/gestionar-reservas',
          queryParams: { filter: 'expired' },
        },
      ],
    },
    {
      id: 'owners',
      title: 'Propietarios',
      icon: 'building-2',
      items: [
        {
          id: 'no-iban',
          label: 'Sin IBAN configurado',
          count: 0,
          urgency: 'warning',
          route: '/admin/dashboard',
          queryParams: { queue: 'no-iban' },
        },
        {
          id: 'new-owners',
          label: 'Nuevos registros (7d)',
          count: 0,
          urgency: 'normal',
          route: '/admin/dashboard',
          queryParams: { queue: 'new-owners' },
        },
        {
          id: 'stale-drafts',
          label: 'Espacios ocultos >30d',
          count: 0,
          urgency: 'normal',
          route: '/admin/dashboard',
          queryParams: { queue: 'stale-drafts' },
        },
      ],
    },
    {
      id: 'system',
      title: 'Sistema',
      icon: 'server',
      items: [
        {
          id: 'stripe',
          label: 'Stripe webhook operativo',
          count: 0,
          urgency: 'normal',
          route: '/admin/dashboard',
          queryParams: { queue: 'stripe' },
        },
        {
          id: 'email',
          label: 'Email transaccional (Resend)',
          count: 0,
          urgency: 'normal',
          route: '/admin/dashboard',
          queryParams: { queue: 'email' },
        },
        {
          id: 'secrets',
          label: 'Secrets configurados',
          count: 0,
          urgency: 'normal',
          route: '/admin/dashboard',
          queryParams: { queue: 'secrets' },
        },
      ],
    },
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // loadKpis() — call once per dashboard visit
  //
  // Cost: 1 aggregation read + monthly platform-expense docs + N active-space reads.
  // Aggregation queries always cost 1 document read regardless of result size.
  // ─────────────────────────────────────────────────────────────────────────────
  async loadKpis(): Promise<void> {
    if (this.loading()) return;
    this.loading.set(true);
    this.loadError.set(null);

    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startTs = Timestamp.fromDate(startOfMonth);

      const [aggResult, assetsSnap, expensesSnap] = await this.inCtx(() => {
        // ── 1 read: aggregation over this month's paid bookings ──────────────
        const bookingsQ = query(
          collection(this.db, 'bookings'),
          where('paymentTimestamp', '>=', startTs),
        );

        // ── N reads: active spaces (small collection, used for distinct owner count) ─
        const assetsQ = query(
          collection(this.db, 'assets'),
          where('active', '==', true),
        );
        const expensesQ = query(
          collection(this.db, 'platform_expenses'),
          where('createdAt', '>=', startTs),
        );

        return Promise.all([
          getAggregateFromServer(bookingsQ, {
            // Composite index: paymentTimestamp + these sum fields
            // (firestore.indexes.json). Do not add extra sum() fields without
            // updating that index.
            commission: sum('commissionAmountCents'),
            gmv:        sum('bookingPriceCents'),
            cnt:        count(),
          }),
          getDocs(assetsQ),
          getDocs(expensesQ),
        ]);
      });

      const commCents  = aggResult.data().commission ?? 0;
      // Historical direct-charge expenses carry connectAccountId; exclude
      // them because Stripe debited the owner Connect account, not TimeFlexRent.
      const feesCents = expensesSnap.docs.reduce((total, expenseDoc) => {
        const expense = expenseDoc.data() as {
          amountCents?: number;
          connectAccountId?: string;
        };
        if (expense.connectAccountId) return total;
        return total + Math.max(0, Number(expense.amountCents ?? 0));
      }, 0);
      const gmvCents   = aggResult.data().gmv        ?? 0;
      const txCount    = aggResult.data().cnt        ?? 0;

      const commNet   = (commCents - feesCents) / 100;
      const commGross = commCents / 100;
      const fees      = feesCents / 100;
      const gmv       = gmvCents / 100;
      const avgTicket = txCount > 0 ? commCents / txCount / 100 : 0;

      // Distinct active owner IDs (one Set.add per asset doc, no extra reads)
      const ownerIds = new Set<string>(
        assetsSnap.docs
          .map((d: import('@angular/fire/firestore').QueryDocumentSnapshot) =>
            (d.data() as { ownerId?: string }).ownerId)
          .filter((id: string | undefined): id is string => !!id),
      );
      const activeOwners = ownerIds.size;

      const fmt = (n: number) =>
        n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      this.kpis.set([
        {
          id: 'net-commission',
          label: 'Comisión neta (mes)',
          value: `${fmt(commNet)} €`,
          deltaLabel: `Bruto ${fmt(commGross)} € − Stripe ${fmt(fees)} €`,
          deltaPositive: commNet > 0,
          sparkline: Array(7).fill(commNet > 0 ? commNet : 0),
        },
        {
          id: 'gmv',
          label: 'GMV mensual',
          value: `${fmt(gmv)} €`,
          deltaLabel: `${txCount} transacción${txCount !== 1 ? 'es' : ''} este mes`,
          deltaPositive: gmv > 0,
          sparkline: Array(7).fill(gmv > 0 ? gmv : 0),
        },
        {
          id: 'avg-ticket',
          label: 'Ticket medio comisión',
          value: txCount > 0 ? `${fmt(avgTicket)} €` : '—',
          deltaLabel: avgTicket >= 15
            ? '✓ Ratio Stripe eficiente (<2 %)'
            : avgTicket > 0
              ? '⚠ Ratio Stripe alto (>2 %) — reservas pequeñas'
              : 'Sin transacciones este mes',
          deltaPositive: avgTicket >= 15 || avgTicket === 0,
          sparkline: Array(7).fill(avgTicket > 0 ? avgTicket : 0),
        },
        {
          id: 'active-owners',
          label: 'Propietarios activos',
          value: String(activeOwners),
          deltaLabel: activeOwners > 0
            ? `${assetsSnap.size} espacio${assetsSnap.size !== 1 ? 's' : ''} publicado${assetsSnap.size !== 1 ? 's' : ''}`
            : 'Ningún espacio publicado aún',
          deltaPositive: activeOwners > 0,
          sparkline: Array(7).fill(activeOwners),
        },
      ]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error cargando métricas';
      this.loadError.set(msg);
    } finally {
      this.loading.set(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // loadOpsColumns() — real Firestore counts for the three admin ops queues.
  //
  // Cost breakdown (per call):
  //   • 3 aggregation reads on bookings (expired, active-form, pending-verif)
  //   • 1 Cloud Function call → getAdminOpsMetrics (counts only — no PII to browser)
  //
  // SEC-3: owner IBAN/phone fields never leave the server — the callable returns
  // only { noIbanCount, newOwnersCount, staleDraftsCount }.
  // ─────────────────────────────────────────────────────────────────────────────
  async loadOpsColumns(): Promise<void> {
    const now   = new Date();
    const nowTs = Timestamp.fromDate(now);

    try {
      // ── Booking queue (aggregation — no PII involved) ─────────────────────
      const [expiredAgg, activeFormAgg, pendingVerifAgg] = await this.inCtx(() =>
        Promise.all([
          getAggregateFromServer(
            query(
              collection(this.db, 'bookings'),
              where('status', '==', 'PENDING_FORMALIZATION'),
              where('formalizationDeadline', '<=', nowTs),
            ),
            { cnt: count() },
          ),
          getAggregateFromServer(
            query(
              collection(this.db, 'bookings'),
              where('status', '==', 'PENDING_FORMALIZATION'),
              where('formalizationDeadline', '>', nowTs),
            ),
            { cnt: count() },
          ),
          getAggregateFromServer(
            query(
              collection(this.db, 'bookings'),
              where('status', '==', 'PENDING_VERIFICATION'),
            ),
            { cnt: count() },
          ),
        ]),
      );

      const expiredCount      = expiredAgg.data().cnt      ?? 0;
      const activeFormCount   = activeFormAgg.data().cnt   ?? 0;
      const pendingVerifCount = pendingVerifAgg.data().cnt ?? 0;

      // ── Owners + drafts queue — via server-side callable (SEC-3 fix) ───────
      // Raw user documents (containing IBANs, phones) never reach the browser.
      const opsCall = this.inCtx(() =>
        httpsCallable<void, AdminOpsMetrics>(this.functions, 'getAdminOpsMetrics'),
      );
      const opsResult = await opsCall();
      const { noIbanCount, newOwnersCount, staleDraftsCount } = opsResult.data;

      // ── Merge into opsColumns signal ──────────────────────────────────────
      this.opsColumns.update(cols => cols.map(col => {
        if (col.id === 'bookings') {
          return {
            ...col,
            items: col.items.map(item => {
              if (item.id === 'pending-form') return { ...item, count: activeFormCount, urgency: activeFormCount > 0 ? 'critical' as const : 'normal' as const };
              if (item.id === 'pending-verif') return { ...item, count: pendingVerifCount, urgency: pendingVerifCount > 0 ? 'warning' as const : 'normal' as const };
              if (item.id === 'expired') return { ...item, count: expiredCount, urgency: expiredCount > 0 ? 'critical' as const : 'normal' as const };
              return item;
            }),
          };
        }
        if (col.id === 'owners') {
          return {
            ...col,
            items: col.items.map(item => {
              if (item.id === 'no-iban') return { ...item, count: noIbanCount, urgency: noIbanCount > 0 ? 'warning' as const : 'normal' as const };
              if (item.id === 'new-owners') return { ...item, count: newOwnersCount };
              if (item.id === 'stale-drafts') return { ...item, count: staleDraftsCount };
              return item;
            }),
          };
        }
        return col;
      }));
    } catch (err) {
      // Non-fatal: ops queue failing shouldn't block the KPI section.
      const msg = err instanceof Error ? err.message : 'Error cargando colas operativas';
      console.warn('[AdminDashboard] loadOpsColumns error:', msg);
    }
  }

}
